import { CheckCircle, MessageSquare, FolderArchive, Wallet, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useScrollReveal, useStaggeredReveal } from "@/hooks/useScrollReveal";
import { useGoogleAnalytics } from "@/hooks/useGoogleAnalytics";

const features = [
  {
    icon: CheckCircle,
    title: "Vous savez exactement quoi faire",
    description: "Chaque étape est claire, expliquée et visible depuis votre espace."
  },
  {
    icon: MessageSquare,
    title: "Vous n'êtes plus seul face à l'administration",
    description: "Un interlocuteur fiscal dédié vous répond directement depuis votre espace."
  },
  {
    icon: FolderArchive,
    title: "Vos impôts sont suivis dans le temps",
    description: "Documents et déclarations restent centralisés pour les années suivantes."
  },
  {
    icon: Wallet,
    title: "Vous gardez le contrôle des coûts",
    description: "Pas d'abonnement. Vous payez uniquement si vous faites votre déclaration."
  }
];

const PlatformFeatures = () => {
  const { ref: headerRef, style: headerStyle } = useScrollReveal({ direction: 'up' });
  const { ref: featuresRef, isVisible, getItemStyle } = useStaggeredReveal(features.length, 100);
  const { trackCTA } = useGoogleAnalytics();

  return (
    <section className="bg-white/50 py-8 md:py-12 lg:py-16" aria-labelledby="platform-title">
      <div className="container-custom">
        {/* Header */}
        <div 
          ref={headerRef}
          style={headerStyle}
          className="text-left mb-8 md:mb-12"
        >
          <h2 id="platform-title" className="text-[1.35rem] sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-heading font-extrabold text-green-primary mb-3 md:mb-4">
            Voilà à quoi votre déclaration d'impôts devrait ressembler.
          </h2>
          <p className="text-sm md:text-sm lg:text-base leading-relaxed text-muted-foreground max-w-2xl">
            Un espace clair, un interlocuteur fiscal qui vous répond, et vos impôts suivis au même endroit, année après année.
          </p>
        </div>

        {/* Content Grid */}
        <div className="grid md:grid-cols-2 gap-6 md:gap-10 lg:gap-14 items-center mb-8 md:mb-12">
          {/* Video Placeholder */}
          <div className="order-1 md:order-1">
            <div className="relative aspect-video bg-green-pastel/20 border border-green-pastel/40 rounded-2xl overflow-hidden flex flex-col items-center justify-center gap-3 hover:bg-green-pastel/30 transition-colors duration-300 cursor-pointer group">
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-white border border-green-pastel/60 flex items-center justify-center shadow-soft group-hover:scale-105 transition-transform duration-300">
                <Play className="w-6 h-6 md:w-7 md:h-7 text-green-primary ml-1" fill="currentColor" />
              </div>
              <div className="text-center">
                <span className="text-sm md:text-base text-gray-700 font-medium block">
                  Aperçu rapide de la plateforme Easy Taxes
                </span>
                <span className="text-xs text-gray-500">
                  (vidéo courte – 20 secondes)
                </span>
              </div>
            </div>
          </div>

          {/* Features */}
          <div 
            ref={featuresRef}
            className="order-2 md:order-2 space-y-3 md:space-y-4"
          >
            {features.map((feature, index) => (
              <div 
                key={index}
                style={getItemStyle(index)}
                className={`flex items-start gap-4 p-5 md:p-0 rounded-2xl md:rounded-none bg-white md:bg-transparent shadow-soft md:shadow-none transition-all duration-300 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
              >
                <div className="flex-shrink-0 w-12 h-12 md:w-10 md:h-10 lg:w-12 lg:h-12 rounded-xl bg-green-pastel/40 flex items-center justify-center">
                  <feature.icon className="w-5 h-5 md:w-4 md:h-4 lg:w-6 lg:h-6 text-green-primary" strokeWidth={1.25} />
                </div>
                <div>
                  <h3 className="text-base md:text-base lg:text-xl font-bold font-heading text-green-primary">
                    {feature.title}
                  </h3>
                  <p className="text-sm md:text-sm lg:text-base leading-relaxed text-muted-foreground">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Button 
            variant="cta" 
            size="lg"
            className="rounded-xl"
            asChild
          >
            <a 
              href="https://taxes.elyx-finance.ch/auth?tab=signup" 
              target="_blank" 
              rel="noopener noreferrer"
              onClick={() => trackCTA('cta_click_signup', 'platform_features', 'features')}
            >
              Créer mon espace gratuitement
            </a>
          </Button>
          <p className="text-xs md:text-sm text-gray-500 mt-3">
            Sans abonnement. Sans engagement. Paiement uniquement si déclaration.
          </p>
        </div>
      </div>
    </section>
  );
};

export default PlatformFeatures;
